export interface ContourPoint {
  x: number;
  y: number;
}

export interface HasContour {
  contour: ReadonlyArray<ContourPoint>;
}

function contourAabb(contour: ReadonlyArray<ContourPoint>): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} | null {
  if (contour.length === 0) return null;
  let minX = contour[0].x;
  let maxX = minX;
  let minY = contour[0].y;
  let maxY = minY;

  for (let i = 1; i < contour.length; i += 1) {
    const p = contour[i];
    if (p.x < minX) minX = p.x;
    else if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    else if (p.y > maxY) maxY = p.y;
  }

  return { minX, maxX, minY, maxY };
}

/**
 * Axis-aligned bounding-box center of a segment's contour. This replaces the
 * removed `Blob2D.{x,y}` box center — the contour is the source of truth.
 */
export function blobCenter(segment: HasContour): ContourPoint {
  const aabb = contourAabb(segment.contour);
  if (aabb === null) return { x: 0, y: 0 };

  return {
    x: (aabb.minX + aabb.maxX) / 2,
    y: (aabb.minY + aabb.maxY) / 2,
  };
}

/**
 * Axis-aligned width/height of a segment's contour. Replaces the removed
 * `Blob2D.{width,height}`.
 */
export function blobExtents(segment: HasContour): {
  width: number;
  height: number;
} {
  const aabb = contourAabb(segment.contour);
  if (aabb === null) return { width: 0, height: 0 };

  return {
    width: aabb.maxX - aabb.minX,
    height: aabb.maxY - aabb.minY,
  };
}

/**
 * Area of a closed contour polygon via the shoelace formula, in the contour's
 * own pixel² units. The contour is treated as implicitly closed (last point
 * connects back to the first); winding direction does not matter — the result
 * is always non-negative. Returns 0 for a degenerate contour (< 3 points).
 *
 * This is the source-of-truth match area for a segmented blob. It is
 * translation-invariant, so the ROI offset applied to contour points does not
 * change the result.
 */
export function contourArea(contour: ReadonlyArray<ContourPoint>): number {
  if (contour.length < 3) return 0;

  let sum = 0;

  for (let i = 0; i < contour.length; i += 1) {
    const a = contour[i];
    const b = contour[(i + 1) % contour.length];
    sum += a.x * b.y - b.x * a.y;
  }

  return Math.abs(sum) / 2;
}
